import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// `<StartConversation>` — the friendly start flow (fn-48.5, R3/R8).
//
// Agent picker (`listAgents` first page) + message + optional subject
// → `startSession` → `onStarted(sessionId)`. The consumer owns
// navigation; this component owns the form.
//
// Persona rules (same family as the rest of the end-user layer):
//   - Names only in the picker — `description` renders ONLY when the
//     adapter provides one (the console adapter never does; the agent
//     prompt must NEVER appear here). NO ids anywhere.
//   - Title convention (R3 write side): an explicit subject (trimmed,
//     non-empty) wins; otherwise the first message, whitespace-
//     collapsed and truncated, becomes `metadata.title` via the
//     adapter's `title` arg. The title is ALWAYS sent.
//   - `matterId` passes through untouched when the host supplies it
//     (v2a embeds have matter context; `/preview` omits it and rides
//     the dev stack's synthetic-matter behavior — a documented
//     constraint, not a prod capability).
//   - Failure keeps the user's inputs on the form with friendly copy;
//     the submit button disables while a start is in flight.
//
// The message field shares the chat composer's load-bearing keydown +
// autosize behaviors (`composer-behaviors.ts`): Enter starts, Shift+
// Enter inserts a newline, an IME composition commit never submits.
//
// Both fn-48 OPTIONAL adapter methods are REQUIRED here — there is no
// degraded start flow without a picker or a `startSession` — so the
// capability guard throws `AdapterCapabilityError` for pre-fn-48
// adapters instead of failing mid-interaction.
import { useEffect, useId, useRef, useState } from 'react';
import { autosizeTextArea, isComposerSendKey } from './internal/chat/composer-behaviors.js';
import { useEndUserAdapter } from './internal/context.js';
import { titleFromMessage } from './internal/conversations/subject.js';
import { CANCEL_LABEL, ERROR_COPY, LOADING_AGENTS, MEMORY_ACCESS_READ_ONLY_LABEL, MEMORY_ACCESS_READ_WRITE_LABEL, NO_AGENTS_AVAILABLE, START_MEMORY_FIRM_LABEL, START_MEMORY_INDICATOR_LABEL, START_MEMORY_MATTER_LABEL, START_MEMORY_USER_LABEL, START_MESSAGE_LABEL, START_PICKER_HEADING, START_SUBJECT_LABEL, START_SUBJECT_PLACEHOLDER, START_SUBMITTING_LABEL, START_SUBMIT_LABEL, } from './internal/copy/copy.js';
/** Canonical scope order + plain-language labels for the read-only memory indicator (ADR 0043). */
const MEMORY_SCOPE_ORDER = ['user', 'firm', 'matter'];
const MEMORY_SCOPE_LABEL = {
    user: START_MEMORY_USER_LABEL,
    firm: START_MEMORY_FIRM_LABEL,
    matter: START_MEMORY_MATTER_LABEL,
};
const MEMORY_ACCESS_LABEL = {
    read_only: MEMORY_ACCESS_READ_ONLY_LABEL,
    read_write: MEMORY_ACCESS_READ_WRITE_LABEL,
};
/**
 * Project an agent's memory map into ordered `{ scope, mode }` chips for the
 * read-only indicator. Returns `[]` when the agent declares no memory (the
 * indicator is then not rendered). The `matter` scope is hidden when the start
 * flow has no matter context (`hasMatterId === false`) — ADR 0043 skips the
 * matter attachment without a `matter_id`, so advertising it would mislead.
 */
function memoryChips(memory, hasMatterId) {
    if (memory === undefined)
        return [];
    return MEMORY_SCOPE_ORDER.filter((scope) => memory[scope] !== undefined && (scope !== 'matter' || hasMatterId)).map((scope) => ({
        scope,
        label: MEMORY_SCOPE_LABEL[scope],
        // Non-null: filtered above. `firm` is only ever `read_only`.
        access: MEMORY_ACCESS_LABEL[memory[scope]],
    }));
}
export function StartConversation(props) {
    const adapter = useEndUserAdapter(['listAgents', 'startSession'], 'StartConversation');
    const idBase = useId();
    // ── Form state — preserved across a failed start by construction ─
    const [agentId, setAgentId] = useState(null);
    const [message, setMessage] = useState('');
    const [subject, setSubject] = useState('');
    const [inFlight, setInFlight] = useState(false);
    const [error, setError] = useState(null);
    // ADR 0043 — there is no per-scope memory picker: memory attachment is
    // defined by the agent, not chosen at start.
    // ── Agent picker data (first page, fetched once per adapter) ────
    const [agents, setAgents] = useState({ status: 'loading' });
    useEffect(() => {
        let live = true;
        setAgents({ status: 'loading' });
        Promise.resolve()
            .then(() => adapter.listAgents())
            .then((page) => {
            if (!live)
                return;
            setAgents({ status: 'ready', agents: page.data });
            // Reconcile the selection with what the picker can SHOW: an
            // adapter swap (or a reload that dropped the agent) must never
            // leave a stale, non-visible agentId submittable.
            setAgentId((prev) => prev !== null && page.data.some((agent) => agent.id === prev) ? prev : null);
        })
            .catch(() => {
            if (!live)
                return;
            setAgents({ status: 'error' });
            setAgentId(null);
        });
        return () => {
            live = false;
        };
    }, [adapter]);
    // Guard the start promise's settle paths after unmount: the failure
    // path must not setState, and the SUCCESS path must not fire
    // `onStarted` into a form the user already left (e.g. Cancel
    // navigated away while the start was in flight).
    const liveRef = useRef(true);
    useEffect(() => {
        liveRef.current = true;
        return () => {
            liveRef.current = false;
        };
    }, []);
    // …and across ADAPTER swaps: a pending startSession from the old
    // adapter must not navigate into a session from the wrong adapter
    // context. Each swap bumps the generation; settle paths compare
    // against the generation captured at submit time.
    const startGenerationRef = useRef(0);
    // biome-ignore lint/correctness/useExhaustiveDependencies: deliberate adapter-swap effect — deps must stay [adapter]; it bumps the generation + cancels in-flight ONLY on a swap, and the ref/setter it touches are stable.
    useEffect(() => {
        startGenerationRef.current += 1;
        setInFlight(false);
    }, [adapter]);
    const canSubmit = agentId !== null && message.trim().length > 0 && !inFlight;
    const submit = () => {
        if (agentId === null)
            return;
        const trimmedMessage = message.trim();
        if (trimmedMessage.length === 0 || inFlight)
            return;
        const trimmedSubject = subject.trim();
        const title = trimmedSubject.length > 0 ? trimmedSubject : titleFromMessage(trimmedMessage);
        setInFlight(true);
        setError(null);
        const generation = startGenerationRef.current;
        const stillCurrent = () => liveRef.current && startGenerationRef.current === generation;
        adapter
            .startSession({
            agentId,
            message: trimmedMessage,
            title,
            ...(props.matterId !== undefined ? { matterId: props.matterId } : {}),
        })
            .then(({ sessionId }) => {
            // A late success after the user left this form (or after an
            // adapter swap) must not navigate into the wrong context.
            if (!stillCurrent())
                return;
            props.onStarted(sessionId);
        })
            .catch(() => {
            // Stay on the form: every input is controlled state, untouched
            // by the failure — the user fixes nothing, just retries.
            if (!stillCurrent())
                return;
            setError(ERROR_COPY.startFailure);
            setInFlight(false);
        });
    };
    const onMessageKeyDown = (e) => {
        if (!isComposerSendKey(e))
            return;
        e.preventDefault();
        submit();
    };
    return (_jsxs("form", { className: "sbui-start", "data-testid": "sbui-start", onSubmit: (e) => {
            e.preventDefault();
            submit();
        }, children: [_jsxs("fieldset", { className: "sbui-start-agents", children: [_jsx("legend", { className: "sbui-start-heading", children: START_PICKER_HEADING }), agents.status === 'loading' && _jsx("p", { className: "sbui-start-note", children: LOADING_AGENTS }), agents.status === 'error' && (_jsx("p", { className: "sbui-start-note", role: "alert", children: ERROR_COPY.generic })), agents.status === 'ready' && agents.agents.length === 0 && (_jsx("p", { className: "sbui-start-note", children: NO_AGENTS_AVAILABLE })), agents.status === 'ready' && agents.agents.length > 0 && (_jsx("div", { className: "sbui-start-agent-cards", children: agents.agents.map((agent) => (_jsxs("label", { className: agentId === agent.id
                                ? 'sbui-start-agent sbui-start-agent--selected'
                                : 'sbui-start-agent', children: [_jsx("input", { type: "radio", className: "sbui-visually-hidden", name: `${idBase}-agent`, checked: agentId === agent.id, onChange: () => setAgentId(agent.id) }), _jsx("span", { className: "sbui-start-agent-name", children: agent.name }), agent.description !== undefined && agent.description.length > 0 && (_jsx("span", { className: "sbui-start-agent-desc", children: agent.description })), (() => {
                                    const chips = memoryChips(agent.memory, props.matterId !== undefined);
                                    if (chips.length === 0)
                                        return null;
                                    return (_jsxs("span", { className: "sbui-start-agent-memory", children: [_jsx("span", { className: "sbui-start-agent-memory-label", children: START_MEMORY_INDICATOR_LABEL }), chips.map((chip) => (_jsxs("span", { className: "sbui-start-agent-memory-chip", children: [chip.label, " \u00B7 ", chip.access] }, chip.scope)))] }));
                                })()] }, agent.id))) }))] }), _jsxs("div", { className: "sbui-start-field", children: [_jsx("label", { className: "sbui-start-label", htmlFor: `${idBase}-message`, children: START_MESSAGE_LABEL }), _jsx("textarea", { id: `${idBase}-message`, className: "sbui-start-message", rows: 3, value: message, onChange: (e) => setMessage(e.currentTarget.value), onInput: (e) => autosizeTextArea(e.currentTarget), onKeyDown: onMessageKeyDown })] }), _jsxs("div", { className: "sbui-start-field", children: [_jsx("label", { className: "sbui-start-label", htmlFor: `${idBase}-subject`, children: START_SUBJECT_LABEL }), _jsx("input", { id: `${idBase}-subject`, className: "sbui-start-subject", type: "text", value: subject, placeholder: START_SUBJECT_PLACEHOLDER, onChange: (e) => setSubject(e.currentTarget.value) })] }), error !== null && (_jsx("p", { className: "sbui-start-error", role: "alert", children: error })), _jsxs("div", { className: "sbui-start-actions", children: [props.onCancel !== undefined && (_jsx("button", { type: "button", className: "sbui-start-cancel", 
                        // Disabled while a start is in flight: the session may
                        // already exist server-side — leaving mid-flight would
                        // orphan it silently (the liveRef guard covers the
                        // unmount race; this keeps the UI honest).
                        disabled: inFlight, onClick: props.onCancel, children: CANCEL_LABEL })), _jsx("button", { type: "submit", className: "sbui-start-submit", disabled: !canSubmit, children: inFlight ? START_SUBMITTING_LABEL : START_SUBMIT_LABEL })] })] }));
}
//# sourceMappingURL=start-conversation.js.map